<app-loading *ngIf="loading"></app-loading>

<section
  class="clinic-page"
  [dir]="isAr ? 'rtl' : 'ltr'"
  [ngStyle]="clinicThemeStyles"
>
  <div class="clinic-bg clinic-bg-one"></div>
  <div class="clinic-bg clinic-bg-two"></div>

  <div class="clinic-shell">
    <!-- Error state -->
    <div *ngIf="pageError" class="clinic-error-card">
      <span class="clinic-error-icon">
        <i class="fa-solid fa-triangle-exclamation"></i>
      </span>
      <h2>{{ pageError }}</h2>
      <p>
        {{
          isAr
            ? ('bbClinic.errors.checkClinicLink' | transloco)
            : ('bbClinic.errors.checkClinicLink' | transloco)
        }}
      </p>
      <button
        *ngIf="warehouseId"
        type="button"
        class="clinic-btn clinic-btn-primary"
        (click)="loadClinic()"
      >
        <i class="fa-solid fa-rotate-right"></i>
        {{ 'bbClinic.common.tryAgain' | transloco }}
      </button>
    </div>

    <ng-container *ngIf="clinic && !pageError">
      <!-- Top bar -->
      <nav class="clinic-topbar">
        <a class="clinic-brand" href="#top" [attr.aria-label]="'bbClinic.aria.clinicHome' | transloco">
          <span class="clinic-brand-logo">
            <img
              [src]="clinic.image_url || '/assets/logo-01.png'"
              [alt]="getClinicName()"
              (error)="onImageError($event)"
            />
          </span>

          <span class="clinic-brand-copy">
            <strong>{{ getClinicName() }}</strong>
            <small>
              {{ 'bbClinic.header.onlineBooking' | transloco }}
            </small>
          </span>
        </a>

        <div class="clinic-topbar-actions">
          <a
            *ngIf="getPhone()"
            class="clinic-icon-action"
            [href]="phoneHref(getPhone())"
            [attr.aria-label]="'bbClinic.actions.callClinic' | transloco"
          >
            <i class="fa-solid fa-phone"></i>
          </a>

          <div class="clinic-language">
            <button
              type="button"
              class="clinic-language-toggle"
              [class.open]="languageDropdownOpen"
              (click)="toggleLanguageDropdown($event)"
            >
              <img
                *ngIf="currentLanguage === 'ar'"
                class="clinic-language-flag"
                src="/assets/ar.png"
                [alt]="'Arabic' | transloco"
              />
              <img
                *ngIf="currentLanguage === 'en'"
                class="clinic-language-flag"
                src="/assets/en.png"
                [alt]="'English' | transloco"
              />
              <img
                *ngIf="currentLanguage === 'fr'"
                class="clinic-language-flag"
                src="/assets/fr.png"
                [alt]="'French' | transloco"
              />
              <i class="fa-solid fa-chevron-down clinic-language-arrow" aria-hidden="true"></i>
            </button>

            <div *ngIf="languageDropdownOpen" class="clinic-language-options">
              <button
                type="button"
                class="clinic-language-option"
                [class.selected]="currentLanguage === 'ar'"
                (click)="setLanguage('ar', $event)"
              >
                <img class="clinic-language-flag" src="/assets/ar.png" [alt]="'Arabic' | transloco" />
                <span>{{ "Arabic" | transloco }}</span>
              </button>
              <button
                type="button"
                class="clinic-language-option"
                [class.selected]="currentLanguage === 'en'"
                (click)="setLanguage('en', $event)"
              >
                <img class="clinic-language-flag" src="/assets/en.png" [alt]="'English' | transloco" />
                <span>{{ "English" | transloco }}</span>
              </button>
              <button
                type="button"
                class="clinic-language-option"
                [class.selected]="currentLanguage === 'fr'"
                (click)="setLanguage('fr', $event)"
              >
                <img class="clinic-language-flag" src="/assets/fr.png" [alt]="'French' | transloco" />
                <span>{{ "French" | transloco }}</span>
              </button>
            </div>
          </div>

          <button
            type="button"
            class="clinic-btn clinic-btn-primary clinic-top-booking"
            (click)="scrollToBooking()"
          >
            <i class="fa-solid fa-calendar-check"></i>
            {{ 'bbClinic.actions.bookAppointment' | transloco }}
          </button>
        </div>
      </nav>

      <!-- Hero -->
      <header id="top" class="clinic-hero">
        <div class="clinic-hero-content">
          <div class="clinic-kicker">
            <span class="clinic-live-dot"></span>
            {{ 'bbClinic.hero.kicker' | transloco }}
          </div>

          <h1>
            {{ 'bbClinic.hero.title' | transloco }}
            <span>{{ 'bbClinic.hero.titleAccent' | transloco }}</span>
          </h1>

          <p *ngIf="getClinicDescription(); else defaultDescription">
            {{ getClinicDescription() }}
          </p>

          <ng-template #defaultDescription>
            <p>
              {{
                isAr
                  ? ('bbClinic.hero.description' | transloco)
                  : ('bbClinic.hero.description' | transloco)
              }}
            </p>
          </ng-template>

          <div class="clinic-hero-actions">
            <button
              type="button"
              class="clinic-btn clinic-btn-primary clinic-btn-large"
              (click)="scrollToServices()"
            >
              <i class="fa-solid fa-stethoscope"></i>
              {{ 'bbClinic.actions.chooseService' | transloco }}
            </button>

            <a
              *ngIf="getPhone()"
              class="clinic-btn clinic-btn-secondary clinic-btn-large"
              [href]="phoneHref(getPhone())"
            >
              <i class="fa-solid fa-phone-volume"></i>
              {{ 'bbClinic.actions.callClinic' | transloco }}
            </a>
          </div>

          <div class="clinic-trust-row">
            <span>
              <i class="fa-solid fa-bolt"></i>
              {{ 'bbClinic.trust.fastBooking' | transloco }}
            </span>
            <span>
              <i class="fa-solid fa-circle-check"></i>
              {{ 'bbClinic.trust.clinicConfirmation' | transloco }}
            </span>
            <span>
              <i class="fa-solid fa-shield-heart"></i>
              {{ 'bbClinic.trust.dataSafe' | transloco }}
            </span>
          </div>
        </div>

        <div class="clinic-hero-visual">
          <div class="clinic-visual-orbit clinic-orbit-one"></div>
          <div class="clinic-visual-orbit clinic-orbit-two"></div>

          <div class="clinic-logo-stage">
            <div class="clinic-logo-halo"></div>
            <img
              [src]="clinic.image_url || '/assets/logo-01.png'"
              [alt]="getClinicName()"
              (error)="onImageError($event)"
            />
          </div>

          <div *ngIf="getClinicAddress()" class="clinic-floating-card clinic-floating-card-address">
            <span><i class="fa-solid fa-location-dot"></i></span>
            <div>
              <small>{{ 'bbClinic.contact.address' | transloco }}</small>
              <strong>{{ getClinicAddress() }}</strong>
            </div>
          </div>

          <a
            *ngIf="getPhone()"
            class="clinic-floating-card clinic-floating-card-phone"
            [href]="phoneHref(getPhone())"
          >
            <span><i class="fa-solid fa-phone"></i></span>
            <div>
              <small>{{ 'bbClinic.contact.clinicPhone' | transloco }}</small>
              <strong dir="ltr">{{ getPhone() }}</strong>
            </div>
          </a>

          <a
            *ngIf="getAnotherPhone()"
            class="clinic-floating-card clinic-floating-card-alt-phone"
            [href]="phoneHref(getAnotherPhone())"
          >
            <span><i class="fa-solid fa-headset"></i></span>
            <div>
              <small>{{ 'bbClinic.contact.anotherPhone' | transloco }}</small>
              <strong dir="ltr">{{ getAnotherPhone() }}</strong>
            </div>
          </a>

          <div
            *ngIf="clinic.website_link || clinic.facebook_link || clinic.instagram_link || getPhone()"
            class="clinic-floating-card clinic-floating-card-social"
          >
            <span><i class="fa-solid fa-share-nodes"></i></span>
            <div class="clinic-floating-social-links">
              <a
                *ngIf="getPhone()"
                [href]="whatsappHref(getPhone())"
                target="_blank"
                rel="noopener noreferrer"
                [attr.aria-label]="'bbClinic.social.whatsapp' | transloco"
              >
                <i class="fa-brands fa-whatsapp"></i>
              </a>
              <a
                *ngIf="clinic.website_link"
                [href]="externalHref(clinic.website_link)"
                target="_blank"
                rel="noopener noreferrer"
                [attr.aria-label]="'bbClinic.social.website' | transloco"
              >
                <i class="fa-solid fa-globe"></i>
              </a>
              <a
                *ngIf="clinic.facebook_link"
                [href]="externalHref(clinic.facebook_link)"
                target="_blank"
                rel="noopener noreferrer"
                [attr.aria-label]="'bbClinic.social.facebook' | transloco"
              >
                <i class="fa-brands fa-facebook-f"></i>
              </a>
              <a
                *ngIf="clinic.instagram_link"
                [href]="externalHref(clinic.instagram_link)"
                target="_blank"
                rel="noopener noreferrer"
                [attr.aria-label]="'bbClinic.social.instagram' | transloco"
              >
                <i class="fa-brands fa-instagram"></i>
              </a>
            </div>
          </div>

          <div class="clinic-floating-card clinic-floating-card-top">
            <span><i class="fa-solid fa-heart-pulse"></i></span>
            <div>
              <small>{{ 'bbClinic.hero.trustedCare' | transloco }}</small>
              <strong>{{ 'bbClinic.hero.forYourPet' | transloco }}</strong>
            </div>
          </div>

          <div class="clinic-floating-card clinic-floating-card-bottom">
            <span><i class="fa-solid fa-calendar-check"></i></span>
            <div>
              <small>{{ 'bbClinic.hero.nextStep' | transloco }}</small>
              <strong>{{ 'bbClinic.hero.chooseAndBook' | transloco }}</strong>
            </div>
          </div>
        </div>
      </header>

      <!-- Contact strip -->
      <section class="clinic-contact-strip">
        <div *ngIf="getClinicAddress()" class="clinic-contact-item">
          <span><i class="fa-solid fa-location-dot"></i></span>
          <div>
            <small>{{ 'bbClinic.contact.address' | transloco }}</small>
            <strong>{{ getClinicAddress() }}</strong>
          </div>
        </div>

        <a
          *ngIf="getPhone()"
          class="clinic-contact-item"
          [href]="phoneHref(getPhone())"
        >
          <span><i class="fa-solid fa-phone"></i></span>
          <div>
            <small>{{ 'bbClinic.contact.clinicPhone' | transloco }}</small>
            <strong dir="ltr">{{ getPhone() }}</strong>
          </div>
        </a>

        <a
          *ngIf="getAnotherPhone()"
          class="clinic-contact-item"
          [href]="phoneHref(getAnotherPhone())"
        >
          <span><i class="fa-solid fa-headset"></i></span>
          <div>
            <small>{{ 'bbClinic.contact.anotherPhone' | transloco }}</small>
            <strong dir="ltr">{{ getAnotherPhone() }}</strong>
          </div>
        </a>

        <div class="clinic-socials">
          <a
            *ngIf="getPhone()"
            [href]="whatsappHref(getPhone())"
            target="_blank"
            rel="noopener noreferrer"
            [attr.aria-label]="'bbClinic.social.whatsapp' | transloco"
          >
            <i class="fa-brands fa-whatsapp"></i>
          </a>
          <a
            *ngIf="clinic.website_link"
            [href]="externalHref(clinic.website_link)"
            target="_blank"
            rel="noopener noreferrer"
            [attr.aria-label]="'bbClinic.social.website' | transloco"
          >
            <i class="fa-solid fa-globe"></i>
          </a>
          <a
            *ngIf="clinic.facebook_link"
            [href]="externalHref(clinic.facebook_link)"
            target="_blank"
            rel="noopener noreferrer"
            [attr.aria-label]="'bbClinic.social.facebook' | transloco"
          >
            <i class="fa-brands fa-facebook-f"></i>
          </a>
          <a
            *ngIf="clinic.instagram_link"
            [href]="externalHref(clinic.instagram_link)"
            target="_blank"
            rel="noopener noreferrer"
            [attr.aria-label]="'bbClinic.social.instagram' | transloco"
          >
            <i class="fa-brands fa-instagram"></i>
          </a>
        </div>
      </section>

      <!-- Main content -->
      <main class="clinic-main-grid">
        <!-- Services -->
        <section id="services-section" class="clinic-services-section">
          <div class="clinic-section-head">
            <div>
              <span class="clinic-section-kicker">
                {{ 'bbClinic.services.kicker' | transloco }}
              </span>
              <h2>{{ 'bbClinic.services.title' | transloco }}</h2>
              <p>
                {{
                  isAr
                    ? ('bbClinic.services.hint' | transloco)
                    : ('bbClinic.services.hint' | transloco)
                }}
              </p>
            </div>

            <span *ngIf="services.length" class="clinic-services-count">
              {{ services.length }}
              <ng-container *ngIf="totalServices && totalServices > services.length">
                / {{ totalServices }}
              </ng-container>
              {{ 'bbClinic.services.countLabel' | transloco }}
            </span>
          </div>

          <div *ngIf="servicesError" class="clinic-alert">
            <span><i class="fa-solid fa-circle-exclamation"></i></span>
            <div>
              <strong>{{ servicesError }}</strong>
              <button type="button" (click)="loadServices()">
                {{ 'bbClinic.common.tryAgain' | transloco }}
              </button>
            </div>
          </div>

          <div
            *ngIf="!loadingServices && services.length === 0 && !servicesError"
            class="clinic-empty-services"
          >
            <span><i class="fa-solid fa-stethoscope"></i></span>
            <h3>
              {{
                isAr
                  ? ('bbClinic.services.emptyTitle' | transloco)
                  : ('bbClinic.services.emptyTitle' | transloco)
              }}
            </h3>
            <p>
              {{
                isAr
                  ? ('bbClinic.services.emptyText' | transloco)
                  : ('bbClinic.services.emptyText' | transloco)
              }}
            </p>
          </div>

          <div *ngIf="services.length > 0" class="clinic-services-grid">
            <article
              *ngFor="let service of services; trackBy: trackByServiceId"
              class="clinic-service-card"
              [class.is-selected]="isServiceSelected(service)"
              (click)="selectService(service)"
              tabindex="0"
              (keydown.enter)="selectService(service)"
            >
              <div class="clinic-service-image">
                <img
                  [src]="getImage(service)"
                  [alt]="getServiceName(service)"
                  (error)="onImageError($event)"
                />

                <span class="clinic-service-pet">
                  {{ getPetLabel(service) }}
                </span>

                <span *ngIf="isServiceSelected(service)" class="clinic-selected-mark">
                  <i class="fa-solid fa-check"></i>
                </span>
              </div>

              <div class="clinic-service-body">
                <div class="clinic-service-title-row">
                  <h3>{{ getServiceName(service) }}</h3>
                  <strong *ngIf="getServicePrice(service) > 0">
                    {{ formatMoney(getServicePrice(service)) }}
                    <small>{{ 'bbClinic.common.currency' | transloco }}</small>
                  </strong>
                </div>

                <p *ngIf="getServiceDescription(service)">
                  {{ getServiceDescription(service) }}
                </p>

                <button
                  type="button"
                  class="clinic-service-action"
                  (click)="$event.stopPropagation(); selectService(service)"
                >
                  <span>
                    {{
                      isServiceSelected(service)
                        ? ('bbClinic.services.selected' | transloco)
                        : ('bbClinic.services.selectAndBook' | transloco)
                    }}
                  </span>
                  <i
                    class="fa-solid"
                    [ngClass]="
                      isServiceSelected(service)
                        ? 'fa-circle-check'
                        : (isAr ? 'fa-arrow-left' : 'fa-arrow-right')
                    "
                  ></i>
                </button>
              </div>
            </article>
          </div>

          <div *ngIf="loadingMoreServices" class="clinic-services-loader">
            <i class="fa-solid fa-spinner fa-spin"></i>
            <span>{{ 'bbClinic.services.loadingMore' | transloco }}</span>
          </div>
        </section>

        <!-- Booking card -->
        <aside id="booking-section" class="clinic-booking-card">
          <div class="clinic-booking-head">
            <div class="clinic-booking-icon">
              <i class="fa-solid fa-calendar-check"></i>
            </div>
            <div>
              <span>{{ 'bbClinic.booking.kicker' | transloco }}</span>
              <h2>{{ 'bbClinic.booking.title' | transloco }}</h2>
            </div>
          </div>

          <div class="clinic-booking-steps">
            <span class="is-active"><b>1</b>{{ 'bbClinic.form.service' | transloco }}</span>
            <i></i>
            <span><b>2</b>{{ 'bbClinic.booking.detailsStep' | transloco }}</span>
            <i></i>
            <span><b>3</b>{{ 'bbClinic.booking.confirmStep' | transloco }}</span>
          </div>

          <div *ngIf="selectedService" class="clinic-selected-summary">
            <img
              [src]="getImage(selectedService)"
              [alt]="getServiceName(selectedService)"
              (error)="onImageError($event)"
            />
            <div>
              <small>{{ 'bbClinic.booking.selectedService' | transloco }}</small>
              <strong>{{ getServiceName(selectedService) }}</strong>
              <span *ngIf="getServicePrice(selectedService) > 0">
                {{ formatMoney(getServicePrice(selectedService)) }}
                {{ 'bbClinic.common.currency' | transloco }}
              </span>
            </div>
            <i class="fa-solid fa-circle-check"></i>
          </div>

          <div *ngIf="bookingError" class="clinic-form-alert">
            <i class="fa-solid fa-circle-exclamation"></i>
            {{ bookingError }}
          </div>

          <form [formGroup]="bookingForm" (ngSubmit)="saveBooking()">
            <div class="clinic-field">
              <label for="pet_service_id">
                {{ 'bbClinic.form.service' | transloco }}
                <em>*</em>
              </label>
              <div class="clinic-control-wrap">
                <i class="fa-solid fa-stethoscope"></i>
                <select
                  id="pet_service_id"
                  formControlName="pet_service_id"
                  (change)="onServiceSelectChange($any($event.target).value)"
                >
                  <option value="">
                    {{ 'bbClinic.form.selectService' | transloco }}
                  </option>
                  <option *ngFor="let service of services" [value]="service.id">
                    {{ getServiceName(service) }}
                    {{
                      getServicePrice(service) > 0
                        ? ' - ' + formatMoney(getServicePrice(service))
                        : ''
                    }}
                  </option>
                </select>
              </div>
              <small *ngIf="shouldShowError('pet_service_id')" class="clinic-field-error">
                {{
                  getFieldError(
                    'pet_service_id',
                    ('bbClinic.form.service' | transloco)
                  )
                }}
              </small>
            </div>

            <div class="clinic-form-row">
              <div class="clinic-field">
                <label for="client_name">
                  {{ 'bbClinic.form.name' | transloco }}
                  <em>*</em>
                </label>
                <div class="clinic-control-wrap">
                  <i class="fa-regular fa-user"></i>
                  <input
                    id="client_name"
                    type="text"
                    formControlName="client_name"
                    [placeholder]="'bbClinic.form.namePlaceholder' | transloco"
                  />
                </div>
                <small *ngIf="shouldShowError('client_name')" class="clinic-field-error">
                  {{
                    getFieldError(
                      'client_name',
                      ('bbClinic.form.name' | transloco)
                    )
                  }}
                </small>
              </div>

              <div class="clinic-field">
                <label for="phone_number">
                  {{ 'bbClinic.form.phone' | transloco }}
                  <em>*</em>
                </label>
                <div class="clinic-control-wrap">
                  <i class="fa-solid fa-mobile-screen-button"></i>
                  <input
                    id="phone_number"
                    type="tel"
                    inputmode="tel"
                    formControlName="phone_number"
                    placeholder="01000000000"
                    dir="ltr"
                  />
                </div>
                <small *ngIf="shouldShowError('phone_number')" class="clinic-field-error">
                  {{
                    getFieldError(
                      'phone_number',
                      ('bbClinic.form.phone' | transloco)
                    )
                  }}
                </small>
              </div>
            </div>

            <div class="clinic-form-row">
              <div class="clinic-field clinic-field-full">
                <label for="pet_type">
                  {{ 'bbClinic.form.petType' | transloco }}
                </label>
                <div class="clinic-control-wrap">
                  <i class="fa-solid fa-paw"></i>
                  <select id="pet_type" formControlName="pet_type">
                    <option value="">
                      {{ 'bbClinic.form.optional' | transloco }}
                    </option>
                    <option value="cat">{{ 'bbClinic.pets.cat' | transloco }}</option>
                    <option value="dog">{{ 'bbClinic.pets.dog' | transloco }}</option>
                    <option value="bird">{{ 'bbClinic.pets.bird' | transloco }}</option>
                    <option value="other">{{ 'bbClinic.pets.other' | transloco }}</option>
                  </select>
                </div>
              </div>

              <div class="clinic-field clinic-field-full">
                <label for="booking_date">
                  {{ 'bbClinic.form.bookingDate' | transloco }}
                  <em>*</em>
                </label>
                <div class="bb-booking-calendar-field clinic-booking-calendar-field" [dir]="isAr ? 'rtl' : 'ltr'">
                  <i class="fa-solid fa-calendar-days clinic-calendar-control-icon"></i>
                  <button
                    type="button"
                    class="bb-booking-calendar-trigger"
                    id="booking_day"
                    (click)="toggleBookingCalendar()"
                    [ngClass]="{
                      'is-invalid': bookingForm.get('booking_date')?.touched && !isSelectedBookingTimeValid(),
                      'is-valid': bookingDateOnly && isSelectedBookingTimeValid()
                    }"
                  >
                    <span>{{ getBookingDateDisplay() }}</span>
                  </button>

                  @if (showBookingCalendar) {
                  <div class="bb-booking-calendar-panel">
                    <div class="bb-booking-calendar-head">
                      <button type="button" (click)="previousBookingCalendarMonth()">
                        <i class="fa-solid" [ngClass]="isAr ? 'fa-chevron-right' : 'fa-chevron-left'"></i>
                      </button>

                      <strong>{{ getBookingCalendarTitle() }}</strong>

                      <button type="button" (click)="nextBookingCalendarMonth()">
                        <i class="fa-solid" [ngClass]="isAr ? 'fa-chevron-left' : 'fa-chevron-right'"></i>
                      </button>
                    </div>

                    <div class="bb-booking-calendar-weekdays">
                      @for (dayName of getBookingCalendarWeekDays(); track dayName) {
                      <span>{{ dayName }}</span>
                      }
                    </div>

                    <div class="bb-booking-calendar-grid">
                      @for (day of getBookingCalendarDays(); track day.date.getTime()) {
                      <button
                        type="button"
                        class="bb-booking-calendar-day"
                        [class.other-month]="!day.isCurrentMonth"
                        [class.today]="day.isToday"
                        [class.selected]="day.isSelected"
                        [disabled]="day.isDisabled"
                        (click)="selectBookingCalendarDate(day.date)"
                      >
                        {{ day.dayNumber }}
                      </button>
                      }
                    </div>
                  </div>
                  }
                </div>

                <input type="hidden" formControlName="booking_date" />

                <div class="bb-time-field clinic-booking-time-field">
                  <div class="bb-arabic-time-picker">
                    <select
                      [value]="getTimeHour12(bookingTime)"
                      (change)="updateBookingTimePart('hour', $event)">
                      @for (hour of hourOptions; track hour) {
                      <option [value]="hour">{{ getTimeOptionLabel(hour) }}</option>
                      }
                    </select>

                    <span>:</span>

                    <select
                      [value]="getTimeMinute(bookingTime)"
                      (change)="updateBookingTimePart('minute', $event)">
                      @for (minute of minuteOptions; track minute) {
                      <option [value]="minute">{{ getTimeOptionLabel(minute) }}</option>
                      }
                    </select>

                    <select
                      [value]="getTimePeriod(bookingTime)"
                      (change)="updateBookingTimePart('period', $event)">
                      <option value="am">{{ getPeriodOptionLabel('am') }}</option>
                      <option value="pm">{{ getPeriodOptionLabel('pm') }}</option>
                    </select>
                  </div>

                  <small class="bb-time-preview">
                    {{ formatTime(bookingTime) }}
                  </small>
                </div>
                <small *ngIf="shouldShowError('booking_date')" class="clinic-field-error">
                  {{
                    getFieldError(
                      'booking_date',
                      ('bbClinic.form.bookingDate' | transloco)
                    )
                  }}
                </small>
              </div>
            </div>

            <button
              type="submit"
              class="clinic-submit-btn"
              [disabled]="bookingLoading || services.length === 0"
            >
              <i
                class="fa-solid"
                [ngClass]="bookingLoading ? 'fa-spinner fa-spin' : 'fa-calendar-check'"
              ></i>
              <span>
                {{
                  bookingLoading
                    ? ('bbClinic.booking.submitting' | transloco)
                    : ('bbClinic.booking.submit' | transloco)
                }}
              </span>
            </button>

            <p class="clinic-booking-note">
              <i class="fa-solid fa-circle-info"></i>
              {{
                isAr
                  ? ('bbClinic.booking.note' | transloco)
                  : ('bbClinic.booking.note' | transloco)
              }}
            </p>
          </form>
        </aside>
      </main>

      <footer class="clinic-footer">
        <div>
          <img src="assets/logo-01.png" [alt]="'bbClinic.footer.logoAlt' | transloco" />
          <span>
            {{ 'bbClinic.footer.poweredBy' | transloco }}
            <strong>{{ 'bbClinic.footer.brand' | transloco }}</strong>
          </span>
        </div>
        <p>© {{ getClinicName() }}</p>
      </footer>

      <button
        type="button"
        class="clinic-mobile-booking"
        (click)="scrollToBooking()"
      >
        <i class="fa-solid fa-calendar-check"></i>
        <span>{{ 'bbClinic.actions.bookNow' | transloco }}</span>
        <i class="fa-solid" [ngClass]="isAr ? 'fa-arrow-left' : 'fa-arrow-right'"></i>
      </button>
    </ng-container>
  </div>
</section>
